import { selectTheme, selectColorScheme } from '../store/slices/theme.js';

import { useAppSelector } from './reduxHooks.js';
import { getThemeColors, ThemeColors, ColorScheme } from './theme.js';

/**
 * Hook to get theme-specific styles for components
 * This hook provides access to the current theme colors based on the theme mode
 */
export function useThemeStyles(): {
  colors: ThemeColors;
  getClassName: (baseClasses?: string) => string;
} {
  const theme = useAppSelector(selectTheme);
  const scheme = useAppSelector(selectColorScheme);
  const colors = getThemeColors(theme, scheme);

  /**
   * Helper function to combine theme classes with base classes
   * @param baseClasses - Optional base classes to include
   * @returns Combined class string
   */
  const getClassName = (baseClasses?: string): string => {
    // Base classes that should always be applied
    const baseClassList = baseClasses ? baseClasses.split(' ') : [];
    
    // Return combined classes as a string
    return baseClassList.join(' ');
  };

  return { colors, getClassName };
}

/**
 * Helper function to create themed component classes
 * @param lightClasses - Classes to apply in light mode
 * @param darkClasses - Classes to apply in dark mode
 * @param theme - Current theme mode
 * @returns Classes for the current theme
 */
export function getThemeClasses(lightClasses: string, darkClasses: string, theme: 'light' | 'dark'): string {
  return theme === 'light' ? lightClasses : darkClasses;
}

/**
 * Helper function to create themed component styles
 * This is useful for components that need to apply styles conditionally based on theme
 */
export function createThemedStyles(theme: 'light' | 'dark', scheme: ColorScheme) {
  const colors = getThemeColors(theme, scheme);
  
  return {
    // Page styles
    page: `${colors.background} ${colors.foreground}`,
    
    // Header styles
    header: `${colors.headerBackground} ${colors.headerText}`,
    headerLink: `${colors.headerText} ${colors.headerHover}`,
    
    // Card styles
    card: `${colors.cardBackground} ${colors.foreground} ${colors.cardBorder}`,
    
    // Form styles
    input: `${colors.inputBackground} ${colors.inputText} ${colors.inputBorder} ${colors.inputPlaceholder}`,
    
    // Button styles
    primaryButton: `${colors.primaryButton} ${colors.primaryButtonHover} ${colors.primaryButtonText}`,
    secondaryButton: `${colors.secondaryButton} ${colors.secondaryButtonHover} ${colors.secondaryButtonText}`,
    
    // Feedback styles
    success: `${colors.success} ${colors.successText}`,
    error: `${colors.error} ${colors.errorText}`,
    warning: `${colors.warning} ${colors.warningText}`,
    info: `${colors.info} ${colors.infoText}`,
  };
}
